import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";
import { sliderData, articles, parsedDate, partners } from "@/lib/utils";
import SEO from "@/components/SEO";

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [index, setIndex] = useState(0);
  const images = [
    {
      id: "gth215",
      title: "L'État fait confiance aux mains locales",
      desc: "Depuis 2015, le PMN a permis aux artisans sénégalais de passer de 22 % à près de 70 % de part de marché dans la commande publique, valorisant ainsi le savoir-faire local.",
      icon: "/hero/bois.png"
    },
    {
      id: "ole294",
      title: "Une ambition vers 100 % local",
      desc: "Le PMN vise une souveraineté artisanale totale en intégrant l'obligation d'utiliser des produits 100 % « Made in Sénégal » dans la commande publique, renforçant ainsi l'économie nationale.",
      icon: "/hero/cuire.png"
    },
    {
      id: "kwv385",
      title: "Un avenir façonné par nos artisans",
      desc: "En soutenant la formation et la formalisation des artisans, le PMN transforme l'artisanat en un pilier du développement économique et social du Sénégal.",
      icon: "/hero/meuble.png"
    },

  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 6);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Partners marquee animation
  const [isHovered, setIsHovered] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    const sequence = async () => {
      while (true) {
        await controls.start({
          x: "-100%",
          transition: { duration: 20, ease: "linear" },
        });
        await controls.set({ x: "100%" });
        await controls.start({
          x: "0%",
          transition: { duration: 0 },
        });
      }
    };

    if (!isHovered) {
      sequence();
    }
  }, [controls, isHovered]);


  const next = () => setIndex((i) => (i + 1) % images.length);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO
        title="Accueil"
        description="Bienvenue sur le site officiel du Projet Mobilier National (PMN). Découvrez notre mission de modernisation de l'artisanat sénégalais et notre engagement envers les artisans locaux."
        keywords="PMN, Projet Mobilier National, Artisanat, Sénégal, Mobilier, Artisans, Modernisation, Développement"
        ogImage="/logo/logo-navbare.png"
      />

      <div className="w-full overflow-x-hidden -mt-7">


      // Carrousel Hero avec ombre pour texte et sans boutons de navigation

        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden w-full">
          <div className="absolute inset-0 bg-[#1E3D1A]/40 z-10" />

          {/* Couche d'image */}
          <div className="w-full h-full z-0">
            <AnimatePresence initial={false} mode="wait">
              <motion.img
                key={images[index].id}
                src={images[index].icon}
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.6 }}
              />
            </AnimatePresence>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80 z-20" />
          <div className="absolute top-[30%] left-[5%] px-4 z-30 w-full">
            <div className="flex flex-col items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.h1
                  key={`title-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="text-2xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg max-w-3xl text-center"
                >
                  {images[index].title}
                </motion.h1>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.p
                  key={`desc-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                  className="text-lg md:text-xl text-white/90 mb-6 drop-shadow-md max-w-3xl text-center"
                >
                  {images[index].desc}
                </motion.p>
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <motion.a
                  href="/missions"
                  className="inline-flex items-center px-6 py-3 bg-[#B89C00] hover:bg-[#B89C00]/90 text-white rounded-md transition-colors duration-300 shadow-lg"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Découvrir notre mission
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </motion.div>
                </motion.a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="relative h-[300px] md:h-[600px] rounded-lg overflow-hidden"
              >
                <img
                  src="/contrat_snhlm_pmn.webp"
                  alt="image pmn"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              <motion.div
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-[#1E3D1A] mb-6">
                  <span className="section-title-senegal">MISSION DU PMN</span>
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Le Projet du Mobilier National s'engage à valoriser et
                  promouvoir l'artisanat local sénégalais. Notre mission est de
                  former et accompagner les artisans dans leur développement
                  professionnel, tout en facilitant leur accès aux marchés.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <span className="text-[#B89C00] mr-2">•</span>
                    <span className="text-gray-600">
                      Accompagner les artisans pour mieux répondre aux exigences
                      de la commande publique, par la formation, le
                      perfectionnement, le financement et l'accès au foncier.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B89C00] mr-2">•</span>
                    <span className="text-gray-600">
                      Coordonner et suivre l'évolution de l'accès des artisans à
                      la commande publique.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B89C00] mr-2">•</span>
                    <span className="text-gray-600">
                      Confirmer l'investissement du génie créateur sénégalais
                      dans le développement économique du pays.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B89C00] mr-2">•</span>
                    <span className="text-gray-600">
                      Vulgariser le projet du mobilier national par la
                      reproduction des prototypes déjà existants.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#B89C00] mr-2">•</span>
                    <span className="text-gray-600">
                      Créer de nouveaux prototypes par l'organisation d'un
                      Concours du Mobilier National.
                    </span>
                  </li>
                </ul>
                <a href="/missions">
                  <Button
                    variant="outline"
                    className="border-[#1E3D1A] text-[#1E3D1A] hover:bg-[#1E3D1A] hover:text-white"
                  >
                    En savoir plus <ChevronRight className="ml-2" />
                  </Button>
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Accomplishments Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-[#1E3D1A] mb-4">
                <span className="section-title-senegal-center">Nos Réalisations</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Des projets concrets pour dynamiser l'artisanat et créer des opportunités durables.
              </p>
            </motion.div>

            <div className="slider-container relative w-full overflow-hidden rounded-2xl">
              <motion.div
                className="flex"
                animate={{
                  x: `-${currentSlide * 100}%`,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
              >
                {sliderData.map((item, index) => (
                  <motion.div
                    key={index}
                    className="slider-item flex-shrink-0 w-full"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="slider-card relative group bg-white rounded-2xl shadow-xl overflow-hidden h-[500px] mx-4">
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                      />
                      <div className="slider-image-container relative h-full w-full overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="slider-image w-full h-full object-cover object-center"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
                      </div>
                      <div className="absolute bottom-5 left-0 right-0 p-8 text-white">
                        <div className="flex items-center mb-4">
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 mr-4 shadow-lg">
                            <item.icon className="w-8 h-8 text-[#B89C00] drop-shadow-lg" />
                          </div>
                          <span className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#B89C00] to-[#FFD700] drop-shadow-lg">
                            {item.stats}
                          </span>
                        </div>
                        <h3 className="md:text-3xl text-xl font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-200 drop-shadow-lg">
                          {item.title}
                        </h3>
                        <p className="md:text-lg text-sm opacity-90 drop-shadow-lg">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Navigation Controls */}
              <div className="absolute -bottom-6 left-0 right-0 flex justify-center items-center space-x-4 py-8">
                <button
                  onClick={() => setCurrentSlide((prev) => (prev - 1 + 5) % 5)}
                  disabled={currentSlide === 0}
                  className={`max-sm:hidden p-3 rounded-full bg-[#1E3D1A] text-white ${currentSlide === 0
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-[#1E3D1A]/90 transition-colors duration-300 shadow-lg"
                    }`}
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>

                <div className="flex space-x-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                  {sliderData.map((dot, index) => (
                    <button
                      key={dot.title}
                      onClick={() => setCurrentSlide(index)}
                      className="relative mx-1"
                    >
                      <div className="max-sm:w-8 w-12 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full ${currentSlide === index
                            ? "bg-[#1E3D1A]"
                            : "bg-gray-400"
                            }`}
                          initial={{ width: "0%" }}
                          animate={{
                            width: "100%",
                          }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentSlide((prev) => (prev + 1) % 5)}
                  disabled={currentSlide === 4}
                  className={`max-sm:hidden p-3 rounded-full bg-[#1E3D1A] text-white ${currentSlide === 4
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-[#1E3D1A]/90 transition-colors duration-300 shadow-lg"
                    }`}
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="bg-gray-50 py-12 px-6 w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl font-extrabold text-[#1E3D1A] mb-4">
                  <span className="section-title-senegal-center">Notre Impact</span>
                </h2>
                <p className="text-gray-600 max-w-3xl mx-auto mb-10">
                  Le PMN a contribué significativement au développement de l'artisanat local et à la création d'emplois durables. 
                  Nos actions ont permis de :
                </p>
              </motion.div>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
              {/* Bloc 1 */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#1E3D1A]/10 text-[#1E3D1A] rounded-full mb-4 mx-auto">
                  <span className="text-xl font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-[#1E3D1A] mb-2">Création d'emplois</h3>
                <p className="text-gray-600 text-sm">Plus de 1000 emplois créés dans le secteur de l'artisanat</p>
              </motion.div>

              {/* Bloc 2 */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#1E3D1A]/10 text-[#1E3D1A] rounded-full mb-4 mx-auto">
                  <span className="text-xl font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-[#1E3D1A] mb-2">Formation professionnelle</h3>
                <p className="text-gray-600 text-sm">Plus de 50 programmes de formation dispensés</p>
              </motion.div>

              {/* Bloc 3 */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#1E3D1A]/10 text-[#1E3D1A] rounded-full mb-4 mx-auto">
                  <span className="text-xl font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-[#1E3D1A] mb-2">Accès aux marchés</h3>
                <p className="text-gray-600 text-sm">Plus de 200 artisans connectés aux marchés publics</p>
              </motion.div>

              {/* Bloc 4 */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white shadow-md rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#1E3D1A]/10 text-[#1E3D1A] rounded-full mb-4 mx-auto">
                  <span className="text-xl font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-[#1E3D1A] mb-2">Valorisation de l'artisanat</h3>
                <p className="text-gray-600 text-sm">Plus de 100 prototypes de mobilier créés</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Actualities Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-8"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-[#1E3D1A] mb-4">
                <span className="section-title-senegal-center">Actualités</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Restez informé des dernières nouvelles et événements du Projet
                Mobilier National
              </p>
            </motion.div>
            <div className="flex items-center justify-center gap-0 my-4">
              <span className="w-6 h-1 bg-green-500" />
              <span className="w-6 h-1 bg-yellow-400" />
              <span className="w-6 h-1 bg-red-500" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.slice(0, 6).map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-white/90 text-sm">
                        {parsedDate(article.date)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#1E3D1A] mb-3 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {article.description}
                    </p>
                    <a
                      href={`/actualites/${article.slug}`}
                      className="inline-flex items-center text-[#B89C00] hover:text-[#B89C00]/80 transition-colors duration-300"
                    >
                      Lire la suite
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center mt-12"
            >
              <a
                href="/actualites"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#1E3D1A] text-white rounded-full hover:bg-[#1E3D1A]/90 transition-colors duration-300 shadow-lg"
              >
                Voir toutes les actualités
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Video Section */}
        <section className="py-20 bg-[#1E3D1A]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  src="https://www.youtube.com/embed/bAp7DoL79Mw?autoplay=1&mute=1&loop=1&playlist=oFP4s_8_Gz4"
                  title="PMN Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">


            <div
              className="relative overflow-hidden"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
              <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />

              <motion.div
                className="flex gap-16"
                animate={controls}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
              >
                {partners.map((partner, index) => (
                  <motion.div
                    key={index}
                    className="flex-shrink-0"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={partner}
                      alt={`Partner ${index + 1}`}
                      className="w-full h-32 object-cover transition-all duration-300"
                    />
                  </motion.div>
                ))}

              </motion.div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 bg-[#1E3D1A] text-white w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl text-center">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Rejoignez le Projet du Mobilier National
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto">
                Que vous soyez artisan, partenaire ou institution, contribuez au
                développement de l'artisanat local
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-[#B89C00] hover:bg-[#B89C00]/90 text-white px-8 py-6 text-lg">
                  <a
                    href="https://pmn.vercel.app/artisans/inscription"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Devenir Artisan
                  </a>
                </Button>
                <Button
                  variant="outline"
                  className="border-[#B89C00] text-[#B89C00]  hover:text-[#B89C00] bg-transparent hover:bg-white/10 px-8 py-6 text-lg"
                >
                  <a
                    href="https://pmn.vercel.app/artisans/inscription"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Devenir Partenaire
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* App Announcement Section */}
        <section className="py-16 bg-gradient-to-b bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="w-full mx-auto bg-gradient-to-br from-blue-50 via-white to-blue-100 rounded-3xl shadow-2xl overflow-hidden relative min-h-[600px]"
            >
              {/* Background decorative elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-200/30 to-blue-300/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-gradient-to-tr from-green-200/20 to-blue-200/30 rounded-full blur-3xl"></div>
                {/* Sunuy Artisan Logo Watermark */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5">
                  <div className="text-8xl font-bold text-blue-600">SA</div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center relative z-10 min-h-[600px]">
                {/* Text Content */}
                <div className="p-8 md:p-12 lg:order-1 flex flex-col justify-center">
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      📱 Disponible maintenant
                    </div>
                  </motion.div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold text-[#1E3D1A] mb-4">
                    📱 Suñuy Artisan
                  </h3>
                  <h4 className="text-xl md:text-2xl text-blue-600 font-semibold mb-6">
                    Votre solution artisanale, sur mobile
                  </h4>
                  <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                    L'application mobile qui connecte les artisans sénégalais avec leurs clients. 
                    Trouvez facilement des professionnels qualifiés, gérez vos commandes et 
                    accédez aux marchés publics en quelques clics.
                  </p>
                  
                  <div className="space-y-4 mb-10">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Accès aux marchés publics</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Gestion des commandes en temps réel</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Réseau d'artisans agréés</span>
                    </div>
                  </div>

                  {/* Store Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => window.open('https://apps.apple.com/sn/app/su%C3%B1uy-artisan/id6745223088?l=fr-FR', '_blank')}
                      className="flex items-center justify-center gap-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-black" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                      </div>
                      <div className="text-left">
                        <div className="text-xs opacity-80">Télécharger sur</div>
                        <div className="text-sm font-bold">App Store</div>
                      </div>
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => window.open('https://play.google.com/store/apps/details?id=com.sunuyartisan.app&hl=fr&pli=1', '_blank')}
                      className="flex items-center justify-center gap-3 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                        </svg>
                      </div>
                      <div className="text-left">
                        <div className="text-xs opacity-80">Disponible sur</div>
                        <div className="text-sm font-bold">Google Play</div>
                      </div>
                    </motion.button>
                  </div>
                </div>
                
                {/* Realistic Phone Mockup */}
                <div className="relative h-[600px] lg:h-[700px] flex items-center justify-center lg:order-2 p-8">
                  <motion.div
                    initial={{ opacity: 0, y: 50, rotateY: 15 }}
                    whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                    transition={{ duration: 1.2, delay: 0.3 }}
                    className="relative transform perspective-1000"
                  >
                    {/* iPhone Frame with realistic proportions */}
                    <div className="relative w-[300px] h-[600px] bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 rounded-[3rem] p-3 shadow-2xl">
                      {/* Phone bezel and buttons */}
                      <div className="absolute -left-1 top-20 w-1 h-12 bg-gray-700 rounded-l-full"></div>
                      <div className="absolute -left-1 top-36 w-1 h-8 bg-gray-700 rounded-l-full"></div>
                      <div className="absolute -left-1 top-48 w-1 h-8 bg-gray-700 rounded-l-full"></div>
                      <div className="absolute -right-1 top-32 w-1 h-16 bg-gray-700 rounded-r-full"></div>
                        
                      {/* Screen with real app screenshot */}
                      <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative shadow-inner">
                        {/* Dynamic Island */}
                        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-28 h-7 bg-black rounded-full z-20"></div>
                        
                        {/* Real App Screenshot */}
                        <img
                          src="/commande_publique.jpeg"
                          alt="Interface Suñuy Artisan"
                          className="w-full h-full object-cover object-center"
                        />
                        
                        {/* Screen Reflection Effect */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 rounded-[2.5rem] pointer-events-none"></div>
                      </div>
                      
                      {/* Phone Shadow */}
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-56 h-12 bg-black/30 rounded-full blur-2xl"></div>
                      
                      {/* Realistic lighting effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-[3rem] pointer-events-none"></div>
                      </div>
                      
                    {/* Floating Decorative Elements */}
                    <motion.div
                      animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute -top-6 -left-6 w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-xl"
                    >
                      <span className="text-white text-2xl">🔧</span>
                    </motion.div>
                    
                    <motion.div
                      animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                      className="absolute -bottom-6 -right-6 w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-xl"
                    >
                      <span className="text-white text-2xl">⚡</span>
                    </motion.div>
                    
                    <motion.div
                      animate={{ x: [0, 10, 0], scale: [1, 1.1, 1] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                      className="absolute top-1/4 -right-10 w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-xl"
                    >
                      <span className="text-white text-lg">📱</span>
                    </motion.div>
                    
                    <motion.div
                      animate={{ x: [0, -8, 0], y: [0, -5, 0] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                      className="absolute top-2/3 -left-8 w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center shadow-xl"
                    >
                      <span className="text-white text-xl">🏡</span>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;